import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Facebook, Instagram, Mail, Phone, Clock, MapPin, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Reveal } from "./Reveal";
import { useSettings } from "../settings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "h-12 rounded-none border-white/20 bg-transparent text-white placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-turquoise focus-visible:border-turquoise";

export const Contact = () => {
  const { settings } = useSettings();
  const socials = [
    { label: "Facebook", href: settings.facebook_url, Icon: Facebook },
    { label: "Instagram", href: settings.instagram_url, Icon: Instagram },
  ];
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll get back to you shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="border-t border-white/10 px-5 py-20 sm:px-8 lg:px-12 lg:py-28">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-turquoise">06 — Contact</p>
          <h2 className="font-heading mt-4 text-3xl font-black uppercase tracking-tight sm:text-4xl lg:text-5xl">
            Talk to a builder, <span className="text-volt">not a call center</span>
          </h2>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-white/60">
            Call, email, or drop us a line here. You reach the same crew that raises the steel.
          </p>

          <div className="mt-10 space-y-5">
            <a href={`tel:${settings.phone}`} data-testid="contact-phone-link" className="flex items-center gap-4 text-white/80 transition-colors hover:text-volt">
              <Phone className="h-5 w-5 text-turquoise" strokeWidth={1.5} />
              <span className="font-heading text-lg font-bold tracking-wide">{settings.phone}</span>
            </a>
            <a href={`mailto:${settings.email}`} data-testid="contact-email-link" className="flex items-center gap-4 text-white/80 transition-colors hover:text-volt">
              <Mail className="h-5 w-5 text-turquoise" strokeWidth={1.5} />
              <span className="font-heading text-lg font-bold tracking-wide">{settings.email}</span>
            </a>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(settings.address)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-address-link"
              className="flex items-center gap-4 text-white/80 transition-colors hover:text-volt"
            >
              <MapPin className="h-5 w-5 shrink-0 text-turquoise" strokeWidth={1.5} />
              <span className="font-heading text-lg font-bold tracking-wide">{settings.address}</span>
            </a>
            <div className="flex items-center gap-4 text-white/50">
              <Clock className="h-5 w-5 text-turquoise" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-[0.25em]">{settings.hours}</span>
            </div>
          </div>

          <div className="mt-10 flex gap-3">
            {socials.map(({ label, href, Icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`social-link-${label.toLowerCase()}`}
                aria-label={label}
                className="flex h-12 w-12 items-center justify-center border border-white/20 text-white/70 transition-colors duration-300 hover:border-volt hover:text-volt"
              >
                <Icon className="h-5 w-5" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form data-testid="contact-form" onSubmit={submit} className="border border-white/10 bg-surface/60 p-6 sm:p-10">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Input data-testid="contact-name-input" required placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} />
              <Input data-testid="contact-email-input" required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} />
            </div>
            <Input data-testid="contact-phone-input" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className={`${inputCls} mt-5`} />
            <Textarea
              data-testid="contact-message-input"
              required
              placeholder="How can we help?"
              value={form.message}
              onChange={set("message")}
              className={`${inputCls} mt-5 h-36 resize-none py-3`}
            />
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="group mt-8 flex w-full items-center justify-center gap-2 bg-white px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-volt disabled:opacity-50"
            >
              {sending ? "Sending..." : "Send Message"}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
